import { Injectable } from '@angular/core';
import {
    PropertySearchPageDto,
    PropertySearchResponseDto,
} from '../dto/property.search.response.dto';
import { PropertySearchCardVM, PropertyBadgeVM } from '../models/property-search-card.vm';
import { PropertySearchPageVM } from '../models/property-search-page.vm';
import { DEFAULT_PAGE_SIZE } from '../models/search-criteria.model';

/**
 * Transforma los DTO crudos de búsqueda en ViewModels listos para la UI.
 * Única fuente de la lógica de presentación (formato de precio, color de
 * badge, parseo de rating). Sin estado ni HTTP — fácilmente testeable.
 */
@Injectable({ providedIn: 'root' })
export class PropertySearchMapper {

    /** Clase CSS de color del badge según el `adLabel` (normalizado). */
    private static readonly BADGE_COLOR: Record<string, string> = {
        VENTA: 'badge--sale',
        RENTA: 'badge--rent',
        ALQUILER: 'badge--rent',
        ANTICRETICO: 'badge--leasing',
        'REMATE BANCARIO': 'badge--auction',
        REMATE: 'badge--auction',
    };

    private static readonly PRICE_FORMAT = new Intl.NumberFormat('es-BO', {
        maximumFractionDigits: 0,
    });

    toPageVM(dto: PropertySearchPageDto | null | undefined): PropertySearchPageVM {
        const content = (dto?.content ?? []).map((it) => this.toCardVM(it));
        return {
            content,
            totalElements: dto?.totalElements ?? content.length,
            pageSize: dto?.pageSize ?? DEFAULT_PAGE_SIZE,
            offset: dto?.offset ?? 0,
        };
    }

    toCardVM(dto: PropertySearchResponseDto): PropertySearchCardVM {
        return {
            propertyId: dto.propertyId,
            image: dto.propertyImage || null,
            typeLabel: dto.segmentLabel ?? '',
            typeIcon: dto.segmentImagePath || null,
            badge: this.toBadge(dto.adLabel),
            location: this.toLocation(dto.zoneName, dto.cityName),
            price: this.toPrice(dto.adPrice),
            rating: this.toRating(dto.optQualification),
            surface: dto.propertySurface || null,
            rooms: dto.roomsNumber ?? null,
            baths: dto.bathroomsNumber ?? null,
        };
    }

    private toBadge(adLabel: string | null | undefined): PropertyBadgeVM | null {
        if (!adLabel) {
            return null;
        }
        const key = this.normalize(adLabel);
        return {
            label: adLabel,
            colorClass: PropertySearchMapper.BADGE_COLOR[key] ?? 'badge--default',
        };
    }

    private toLocation(zoneName?: string, cityName?: string): string {
        return [zoneName, cityName].filter((p) => !!p && p.trim()).join(', ');
    }

    private toPrice(adPrice: string | null | undefined): string {
        if (!adPrice) {
            return '';
        }
        const value = Number(adPrice);
        return Number.isFinite(value)
            ? `Bs. ${PropertySearchMapper.PRICE_FORMAT.format(value)}`
            : `Bs. ${adPrice}`;
    }

    private toRating(optQualification: string | null | undefined): number | null {
        if (!optQualification) {
            return null;
        }
        const value = parseFloat(optQualification);
        return Number.isFinite(value) ? value : null;
    }

    /** Mayúsculas + sin acentos, para keyear el mapa de colores. */
    private normalize(label: string): string {
        return label
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim()
            .toUpperCase();
    }
}
